import { z } from 'zod';

/**
 * The Exchange Rates module's contracts.
 *
 * Form validation is a courtesy; the service is the source of truth. All
 * user-facing messages are written in the language a front-desk operator
 * would understand, not the database's.
 */

const RATE_PATTERN = /^\d+(\.\d{1,6})?$/u;
const CURRENCY_PATTERN = /^[A-Z]{3}$/u;
const SOURCE_MAX = 64;
const MAX_RATE_DECIMAL_PLACES = 6;

const currencyCodeSchema = z
  .string()
  .trim()
  .min(1, 'Choose a currency')
  .regex(CURRENCY_PATTERN, 'A currency code must be 3 letters, such as AUD');

const rateStringSchema = z
  .string()
  .trim()
  .min(1, 'Enter an exchange rate')
  .regex(
    RATE_PATTERN,
    `Enter a positive number with up to ${String(MAX_RATE_DECIMAL_PLACES)} decimal places, such as 1.2345`,
  )
  .refine((value) => Number(value) > 0, {
    message: 'The exchange rate must be greater than zero',
  });

const isoTimestampSchema = z
  .string()
  .optional()
  .refine((value) => value === undefined || value === '' || !Number.isNaN(Date.parse(value)), {
    message: 'Enter a valid date or timestamp, such as 2026-01-01 or 2026-01-01T00:00:00Z',
  });

const sourceSchema = z
  .string()
  .trim()
  .min(1, 'Enter the rate source')
  .max(SOURCE_MAX, `Keep the source under ${String(SOURCE_MAX)} characters`);

export const exchangeRateFormInput = z
  .object({
    baseCurrency: currencyCodeSchema,
    quoteCurrency: currencyCodeSchema,
    rate: rateStringSchema,
    source: sourceSchema,
    effectiveAt: isoTimestampSchema,
    expiresAt: isoTimestampSchema,
  })
  .superRefine((value, ctx) => {
    if (value.baseCurrency === value.quoteCurrency) {
      ctx.addIssue({
        code: 'custom',
        message: 'Base and quote currencies must be different',
        path: ['quoteCurrency'],
      });
    }
    if (
      value.effectiveAt &&
      value.expiresAt &&
      Date.parse(value.expiresAt) <= Date.parse(value.effectiveAt)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Expiry must be after the effective time',
        path: ['expiresAt'],
      });
    }
  });

export type ExchangeRateFormInput = z.infer<typeof exchangeRateFormInput>;

export const exchangeRateDetailSchema = z.object({
  id: z.string(),
  baseCurrency: z.string(),
  quoteCurrency: z.string(),
  rate: z.string(),
  source: z.string(),
  effectiveAt: z.string(),
  retrievedAt: z.string(),
  expiresAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
});

export type ExchangeRateDetail = z.infer<typeof exchangeRateDetailSchema>;

export const exchangeRateRow = exchangeRateDetailSchema.partial().extend({
  id: z.string(),
});

export type ExchangeRateRow = z.infer<typeof exchangeRateRow>;
